'use client';

import React, { forwardRef, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import * as XLSX from 'xlsx';
import { Button } from '../components/ui/Button';
import { useConfirm } from '../components/ui/ConfirmDialog';
import { useToastActions } from '../components/ui/Toast';
import { useWorkflowListStore, SavedWorkflow } from '../stores/workflowListStore';
import { workflowApi as _workflowApi, type ExcelStylesPayload } from '../services/api';
import { getNodeDisplayLabel } from '../utils/nodeDisplayLabel';
import { DataTable } from '../components/workflow/DataArea';
import { DiffResultView, type DiffResultViewRef } from '../components/workflow/DiffResultView';
import {
  Plus,
  MagnifyingGlass,
  FlowArrow,
  Clock,
  XCircle,
  PencilSimple,
  Trash,
  Play,
  DotsThree,
  FileXls,
  Table,
  Spinner,
  Calendar,
  AppWindow,
  DownloadSimple,
  WarningCircle,
  Lightning,
  ArrowsLeftRight,
  ListChecks,
} from '@phosphor-icons/react';

// ── Status Badge ─────────────────────────────────────────────────────────────
function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    active:   'bg-emerald-50 text-emerald-700 border-emerald-200',
    inactive: 'bg-zinc-100 text-zinc-600 border-zinc-200',
    draft:    'bg-amber-50 text-amber-700 border-amber-200',
  };
  const { t } = useTranslation();
  const labels: Record<string, string> = {
    active: t('workflow.statusActive') || '已启用', 
    inactive: t('workflow.statusInactive') || '已停用', 
    draft: t('workflow.statusDraft') || '草稿',
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${styles[status] || styles.inactive}`}>
      {labels[status] || status}
    </span>
  );
}

// ── Visibility Badge ─────────────────────────────────────────────────────────
function VisibilityBadge({ visibility, isOwner }: { visibility?: string; isOwner?: boolean }) {
  const { t } = useTranslation();
  
  if (visibility === 'team') {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200" title={t('workflow.visibilityTeamDesc') || '团队可见'}>
        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
          <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
        </svg>
        {t('workflow.visibilityTeam') || '团队'}
        {!isOwner && <span className="ml-1 text-blue-500">({t('workflow.readonly') || '只读'})</span>}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-zinc-100 text-zinc-600 border border-zinc-200" title={t('workflow.visibilityPrivateDesc') || '仅自己可见'}>
      <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
      </svg>
      {t('workflow.visibilityPrivate') || '私有'}
    </span>
  );
}

// ── Run Workflow Modal ────────────────────────────────────────────────────────
type RunState = 'idle' | 'uploading' | 'running' | 'done' | 'error';

// 输入节点类型
const INPUT_NODE_TYPES = ['load_excel', 'load_csv', 'load_table', 'online_sheet'];

interface InputNodeInfo {
  id: string;
  type: string;
  label: string;
  file: File | null;
  tableFormat?: 'excel' | 'csv';
}

interface ExecutionResultItem {
  nodeId: string;
  label: string;
  data: Record<string, unknown>[];
  columns: string[];
  rowCount: number;
  metadata?: Record<string, unknown>;
  isDiffResult?: boolean;
  excelStyles?: ExcelStylesPayload;
}

function RunWorkflowModal({
  isOpen,
  onClose,
  workflow,
}: {
  isOpen: boolean;
  onClose: () => void;
  workflow?: SavedWorkflow;
}) {
  const { t, i18n } = useTranslation();
  const toast = useToastActions();
  const { incrementExecutionCount } = useWorkflowListStore();

  const [runState, setRunState] = useState<RunState>('idle');
  const [inputNodes, setInputNodes] = useState<InputNodeInfo[]>([]);
  const [draggingNodeId, setDraggingNodeId] = useState<string | null>(null);
  const [executionResults, setExecutionResults] = useState<ExecutionResultItem[]>([]);
  const [activeResultTab, setActiveResultTab] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const [fieldValidation, setFieldValidation] = useState<{
    valid: boolean;
    requiredFields: string[];
    missingFields: string[];
    message?: string;
  } | null>(null);
  const diffViewRef = useRef<DiffResultViewRef | null>(null);

  // 从表格流中提取输入节点
  React.useEffect(() => {
    if (!workflow) {
      setInputNodes([]);
      return;
    }
    const nodes: InputNodeInfo[] = workflow.nodes
      .filter((n) => INPUT_NODE_TYPES.includes(n.type))
      .map((n) => {
        const params = (n.data?.config?.params ?? {}) as Record<string, unknown>;
        const tableFormat =
          n.type === 'load_table' ? (params.tableFormat === 'csv' ? ('csv' as const) : ('excel' as const)) : undefined;
        const defaultLabel =
          n.type === 'load_csv'
            ? t('workflow.csvInput')
            : n.type === 'load_excel'
              ? t('workflow.excelInput')
              : n.type === 'load_table'
                ? t('workflow.tableInput')
                : n.type;
        const display = getNodeDisplayLabel(n.type, n.data?.label, t, i18n);
        return {
          id: n.id,
          type: n.type,
          label: display || defaultLabel,
          file: null,
          tableFormat,
        };
      });
    setInputNodes(nodes);
  }, [workflow, t, i18n]);

  const resetState = () => {
    setRunState('idle');
    setInputNodes((prev) => prev.map((n) => ({ ...n, file: null })));
    setExecutionResults([]);
    setActiveResultTab(0);
    setErrorMessage('');
    setFieldValidation(null);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleFileSelect = async (nodeId: string, file: File) => {
    const ext = file.name.toLowerCase();
    if (!ext.endsWith('.xlsx') && !ext.endsWith('.xls') && !ext.endsWith('.csv')) {
      toast.error(t('workflow.supportedFormatsError'));
      return;
    }
    setInputNodes((prev) =>
      prev.map((n) => (n.id === nodeId ? { ...n, file } : n))
    );
    setRunState('idle');
    setExecutionResults([]);
    setFieldValidation(null);

    // 如果工作流有需要字段，读取文件并校验
    if (workflow?.requiredFields && workflow.requiredFields.length > 0) {
      try {
        const columns = await extractColumnsFromFile(file);
        if (columns.length > 0) {
          const result = await _workflowApi.validateWorkflowFields(workflow.id, columns);
          if (result && 'data' in result) {
            setFieldValidation(result.data);
          } else {
            setFieldValidation(result as { valid: boolean; requiredFields: string[]; missingFields: string[]; message?: string });
          }
        }
      } catch (e) {
        console.error('Field validation error:', e);
      }
    }
  };

  // 从文件中提取列名
  const extractColumnsFromFile = async (file: File): Promise<string[]> => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = e.target?.result;
          if (!data) {
            resolve([]);
            return;
          }
          
          const ext = file.name.toLowerCase();
          if (ext.endsWith('.csv')) {
            // CSV 文件：解析第一行
            const text = data as string;
            const firstLine = text.split('\n')[0];
            const columns = firstLine.split(',').map(c => c.trim().replace(/^["']|["']$/g, ''));
            resolve(columns);
          } else {
            // Excel 文件
            const workbook = XLSX.read(data, { type: 'binary' });
            const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
            const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 }) as unknown[][];
            if (jsonData.length > 0) {
              const columns = (jsonData[0] as string[]).map(c => String(c).trim());
              resolve(columns);
            } else {
              resolve([]);
            }
          }
        } catch (err) {
          console.error('Extract columns error:', err);
          resolve([]);
        }
      };
      reader.onerror = () => resolve([]);
      
      if (file.name.toLowerCase().endsWith('.csv')) {
        reader.readAsText(file);
      } else {
        reader.readAsBinaryString(file);
      }
    });
  };

  const handleDrop = (e: React.DragEvent, nodeId: string) => {
    e.preventDefault();
    setDraggingNodeId(null);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(nodeId, file);
  };

  const handleClearFile = (nodeId: string) => {
    setInputNodes((prev) =>
      prev.map((n) => (n.id === nodeId ? { ...n, file: null } : n))
    );
  };

  const allFilesSelected = inputNodes.length > 0 && inputNodes.every((n) => n.file !== null);

  const handleExecute = async () => {
    if (!allFilesSelected || !workflow) return;

    setRunState('uploading');
    try {
      const files = inputNodes.map((n) => ({ nodeId: n.id, file: n.file! }));
      const result = await _workflowApi.runWorkflow(workflow.id, files, {
        name: workflow.name,
        description: workflow.description,
        nodes: workflow.nodes,
        edges: workflow.edges,
      });
      setRunState('running');
      await new Promise((r) => setTimeout(r, 400));

      // 与设计页面一致：构建 executionResults（分组 / 单结果 / 差异对比）
      if (result.is_grouped && result.grouped_results && result.grouped_results.length > 0) {
        const groupItems: ExecutionResultItem[] = result.grouped_results.map((g, idx) => ({
          nodeId: `__group_${idx}__`,
          label: `${t('workflow.groupPrefix')} ${g.group_key}`,
          data: g.rows,
          columns: g.columns,
          rowCount: g.rowCount,
        }));
        groupItems.push({
          nodeId: '__merged__',
          label: t('workflow.mergedResult'),
          data: result.rows,
          columns: result.columns,
          rowCount: result.rowCount,
          excelStyles: result.excelStyles,
        });
        setExecutionResults(groupItems);
      } else {
        const isDiff = result.columns.includes('diff_type');
        setExecutionResults([{
          nodeId: '__result_0__',
          label: isDiff ? t('workflow.diffResult') : t('workflow.executionResult'),
          data: result.rows,
          columns: result.columns,
          rowCount: result.rowCount,
          metadata: result.metadata,
          isDiffResult: isDiff,
          excelStyles: result.excelStyles,
        }]);
      }
      setActiveResultTab(0);
      setRunState('done');
      incrementExecutionCount(workflow.id);
      toast.success(`表格流执行完成，共 ${result.rowCount} 行结果`);
    } catch (err: unknown) {
      setErrorMessage(err instanceof Error ? err.message : '执行失败，请重试');
      setRunState('error');
    }
  };

  const handleDownloadResult = async (
    index: number,
    exportOverride?: { columns: string[]; rows: string[][] }
  ) => {
    const result = executionResults[index];
    if (!result) return;

    const exportNode = workflow?.nodes.find(
      (n) => n.type === 'export'
    );
    const nodeConfig = exportNode?.data?.config?.params || {};
    const exportFormat = (nodeConfig.format as 'excel' | 'csv') || 'excel';
    const baseFileName = (nodeConfig.fileName as string) || workflow?.name || 'result';

    if (exportFormat === 'excel' && !exportOverride && result.excelStyles) {
      try {
        const blob = await _workflowApi.exportExcel({
          columns: result.columns,
          rows: result.data,
          excelStyles: result.excelStyles,
          fileName: `${baseFileName}.xlsx`,
          sheetName: (nodeConfig.sheetName as string) || undefined,
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${baseFileName}.xlsx`;
        a.click();
        URL.revokeObjectURL(url);
        return;
      } catch (err) {
        console.error('export-excel failed, fallback to client-side XLSX', err);
      }
    }

    let aoa: unknown[][];
    if (exportOverride) {
      aoa = [exportOverride.columns, ...exportOverride.rows];
    } else {
      aoa = [
        result.columns,
        ...result.data.map((row) => result.columns.map((c) => (row[c] ?? '') as string)),
      ];
    }

    if (exportFormat === 'csv') {
      const separator = (nodeConfig.separator as string) || ',';
      const encoding = (nodeConfig.encoding as string) || 'utf-8';

      const csvContent = aoa
        .map((row) =>
          row
            .map((cell) => {
              const str = String(cell ?? '');
              if (str.includes(separator) || str.includes('\n') || str.includes('"')) {
                return `"${str.replace(/"/g, '""')}"`;
              }
              return str;
            })
            .join(separator)
        )
        .join('\n');

      let blob: Blob;
      if (encoding === 'utf-8') {
        blob = new Blob([new Uint8Array([0xEF, 0xBB, 0xBF]), csvContent], {
          type: 'text/csv;charset=utf-8;',
        });
      } else {
        blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      }

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${baseFileName}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } else {
      const sheetName = (nodeConfig.sheetName as string) || 'Sheet1';
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
      const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([buf], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${baseFileName}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  if (!isOpen || !workflow) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-zinc-900/40 backdrop-blur-sm flex items-center justify-center p-4"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: 'spring', stiffness: 300, damping: 25 }}
          className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-zinc-100 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                <AppWindow className="w-5 h-5 text-emerald-600" weight="fill" />
              </div>
              <div>
                <h3 className="font-semibold text-zinc-900">{t('workflow.applyWorkflow')}</h3>
                <p className="text-xs text-zinc-500 max-w-[24ch] truncate">{workflow.name}</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 rounded-lg transition-colors"
            >
              <XCircle className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-6 space-y-5">
            {/* Input Files Section */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">
                  {t('workflow.step1Upload')}
                </p>
                <span className="text-[10px] text-zinc-400">
                  {t('workflow.inputNodesCount', { count: inputNodes.length })}
                </span>
              </div>

              {inputNodes.length === 0 ? (
                <div className="p-6 bg-zinc-50 rounded-2xl border border-zinc-200 text-center">
                  <WarningCircle className="w-8 h-8 text-amber-400 mx-auto mb-2" weight="fill" />
                  <p className="text-sm text-zinc-600">{t('workflow.noInputNodes')}</p>
                  <p className="text-xs text-zinc-400 mt-1">{t('workflow.addInputNodesHint')}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {inputNodes.map((node, index) => (
                    <InputFileUploader
                      key={node.id}
                      node={node}
                      index={index}
                      isDragging={draggingNodeId === node.id}
                      onDragStart={() => setDraggingNodeId(node.id)}
                      onDragEnd={() => setDraggingNodeId(null)}
                      onDrop={(e) => handleDrop(e, node.id)}
                      onFileSelect={(file) => handleFileSelect(node.id, file)}
                      onClear={() => handleClearFile(node.id)}
                      disabled={runState !== 'idle'}
                    />
                  ))}
                </div>
              )}

              {/* 必需字段提示 */}
              {workflow?.requiredFields && workflow.requiredFields.length > 0 && (
                <div className="mt-4 p-3 bg-blue-50 rounded-xl border border-blue-100">
                  <div className="flex items-center gap-2 mb-2">
                    <ListChecks className="w-4 h-4 text-blue-600" weight="fill" />
                    <span className="text-xs font-medium text-blue-700">{t('workflow.requiredFieldsDesc')}</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {workflow.requiredFields.map((field) => (
                      <span
                        key={field}
                        className={`px-2 py-0.5 rounded text-[10px] font-medium ${
                          fieldValidation?.missingFields?.includes(field)
                            ? 'bg-red-100 text-red-700'
                            : fieldValidation?.valid
                              ? 'bg-emerald-100 text-emerald-700'
                              : 'bg-blue-100 text-blue-700'
                        }`}
                      >
                        {field}
                        {fieldValidation?.missingFields?.includes(field) && ' ✗'}
                        {fieldValidation?.valid && ' ✓'}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* 字段缺失警告 */}
              {fieldValidation && !fieldValidation.valid && fieldValidation.missingFields.length > 0 && (
                <div className="mt-4 p-4 bg-red-50 rounded-xl border border-red-200">
                  <div className="flex items-start gap-3">
                    <WarningCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" weight="fill" />
                    <div>
                      <p className="text-sm font-medium text-red-700">{t('workflow.fieldsMissing')}</p>
                      <p className="text-xs text-red-600 mt-1">{t('workflow.fieldsMissingDesc')}</p>
                      <div className="mt-2 flex flex-wrap gap-1">
                        {fieldValidation.missingFields.map((field) => (
                          <span
                            key={field}
                            className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-[10px] font-medium"
                          >
                            {field}
                          </span>
                        ))}
                      </div>
                      <p className="text-xs text-red-500 mt-2">{t('workflow.continueExecute')}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Step 2: Execute */}
            {allFilesSelected && (
              <div>
                <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-3">
                  {t('workflow.step2Execute')}
                </p>

                <AnimatePresence mode="wait">
                  {runState === 'idle' && (
                    <motion.div key="idle" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                      <div className="p-3 bg-zinc-50 rounded-xl border border-zinc-200 text-xs text-zinc-500">
                        {t('workflow.filesReady')} {t('workflow.inputNodesCount', { count: inputNodes.length })}。
                        {t('workflow.execute')}<span className="font-medium text-zinc-700"> "{workflow.name}"</span>。
                      </div>
                    </motion.div>
                  )}

                  {(runState === 'uploading' || runState === 'running') && (
                    <motion.div key="running" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                      className="flex flex-col items-center py-6 gap-3"
                    >
                      <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}>
                        <Spinner className="w-8 h-8 text-emerald-500" weight="bold" />
                      </motion.div>
                      <p className="text-sm font-medium text-zinc-700">
                        {runState === 'uploading' ? t('workflow.uploading') : t('workflow.executing')}
                      </p>
                      <p className="text-xs text-zinc-400">{t('workflow.pleaseWait')}</p>
                    </motion.div>
                  )}

                  {runState === 'done' && executionResults.length > 0 && (
                    <motion.div key="done" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                      className="flex flex-col min-h-0 -mx-2"
                    >
                      {/* 结果标签页（与设计页面一致） */}
                      <div className="flex items-center gap-1 px-2 mb-2 overflow-x-auto">
                        {executionResults.map((r, idx) => (
                          <button
                            key={r.nodeId}
                            onClick={() => setActiveResultTab(idx)}
                            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-all whitespace-nowrap flex-shrink-0 ${
                              activeResultTab === idx
                                ? 'bg-white text-zinc-900 shadow-sm border border-zinc-200'
                                : 'text-zinc-500 hover:text-zinc-700 hover:bg-white/60'
                            }`}
                          >
                            {r.isDiffResult ? (
                              <ArrowsLeftRight className="w-3.5 h-3.5" weight="fill" />
                            ) : (
                              <Lightning className="w-3.5 h-3.5" weight="fill" />
                            )}
                            {r.label}
                            <span className={`px-1.5 py-px rounded text-[10px] font-semibold ${
                              r.isDiffResult ? 'bg-purple-100 text-purple-700' : 'bg-amber-100 text-amber-700'
                            }`}>
                              {r.rowCount.toLocaleString()}
                            </span>
                          </button>
                        ))}
                      </div>
                      <div className="flex items-center justify-between gap-2 px-2 mb-2">
                        <span className="text-[10px] text-emerald-600 font-medium tabular-nums">
                          {t('workflow.totalRows', { count: executionResults[activeResultTab]?.rowCount ?? 0 })}
                        </span>
                        <button
                          onClick={() => {
                            const r = executionResults[activeResultTab];
                            const exportOverride = r?.isDiffResult
                              ? diffViewRef.current?.getExportData()
                              : undefined;
                            handleDownloadResult(activeResultTab, exportOverride);
                          }}
                          className="flex items-center gap-1 px-2 py-0.5 text-[10px] text-zinc-500 hover:text-emerald-600 hover:bg-emerald-50 rounded-md transition-colors border border-zinc-200 hover:border-emerald-200"
                        >
                          <DownloadSimple className="w-3 h-3" weight="bold" />
                          {t('common.download')}
                        </button>
                      </div>
                      {/* 结果内容区域 */}
                      <div className="flex-1 min-h-[200px] max-h-[320px] overflow-auto rounded-xl border border-zinc-200 bg-white">
                        {executionResults[activeResultTab] && (
                          executionResults[activeResultTab].isDiffResult ? (
                            <DiffResultView
                              ref={diffViewRef}
                              data={executionResults[activeResultTab].data}
                              columns={executionResults[activeResultTab].columns}
                              metadata={executionResults[activeResultTab].metadata}
                            />
                          ) : (
                            <DataTable
                              data={executionResults[activeResultTab].data}
                              columns={executionResults[activeResultTab].columns}
                            />
                          )
                        )}
                      </div>
                    </motion.div>
                  )}

                  {runState === 'error' && (
                    <motion.div key="error" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                      className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl"
                    >
                      <WarningCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" weight="fill" />
                      <div>
                        <p className="text-sm font-medium text-red-700">{t('workflow.executionFailed')}</p>
                        <p className="text-xs text-red-500 mt-0.5">{errorMessage}</p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-zinc-50/50 border-t border-zinc-100 flex items-center justify-between gap-3 flex-shrink-0">
            <Button variant="secondary" onClick={handleClose}>
              {runState === 'done' ? t('common.close') : t('common.cancel')}
            </Button>

            <div className="flex gap-2">
              {/* {runState === 'done' && executionResults.length > 0 && (
                <Button
                  variant="primary"
                  leftIcon={<DownloadSimple className="w-4 h-4" />}
                  onClick={() => {
                    const r = executionResults[activeResultTab];
                    const exportOverride = r?.isDiffResult ? diffViewRef.current?.getExportData() : undefined;
                    handleDownloadResult(activeResultTab, exportOverride);
                  }}
                >
                  下载结果
                </Button>
              )} */}
              {runState !== 'done' && (
                <Button
                  variant="accent"
                  leftIcon={
                    runState === 'uploading' || runState === 'running'
                      ? <Spinner className="w-4 h-4 animate-spin" weight="bold" />
                      : <Play className="w-4 h-4" weight="fill" />
                  }
                  onClick={handleExecute}
                  disabled={!allFilesSelected || runState === 'uploading' || runState === 'running'}
                >
                  {runState === 'uploading' ? t('common.uploading') : runState === 'running' ? t('workflow.running') : t('workflow.execute')}
                </Button>
              )}
              {runState === 'error' && (
                <Button variant="secondary" onClick={resetState}>
                  {t('common.retry')}
                </Button>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// ── Input File Uploader Component ─────────────────────────────────────────────

interface InputFileUploaderProps {
  node: InputNodeInfo;
  index: number;
  isDragging: boolean;
  onDragStart: () => void;
  onDragEnd: () => void;
  onDrop: (e: React.DragEvent) => void;
  onFileSelect: (file: File) => void;
  onClear: () => void;
  disabled?: boolean;
}

function InputFileUploader({
  node,
  index,
  isDragging,
  onDragStart,
  onDragEnd,
  onDrop,
  onFileSelect,
  onClear,
  disabled,
}: InputFileUploaderProps) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isExcel =
    node.type === 'load_excel' || (node.type === 'load_table' && node.tableFormat !== 'csv');
  const accept = isExcel ? '.xlsx,.xls' : '.csv';

  const handleClick = () => {
    if (!disabled) fileInputRef.current?.click();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onFileSelect(file);
    e.target.value = '';
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className="relative"
    >
      {/* Node Label */}
      <div className="flex items-center gap-2 mb-1.5">
        <span className="text-xs font-medium text-zinc-600">{node.label}</span>
        <span className="text-[10px] text-zinc-400">({isExcel ? 'Excel' : 'CSV'})</span>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleInputChange}
        className="hidden"
        disabled={disabled}
      />

      {!node.file ? (
        <div
          onDragOver={(e) => { e.preventDefault(); onDragStart(); }}
          onDragLeave={onDragEnd}
          onDrop={onDrop}
          onClick={handleClick}
          className={`
            border-2 border-dashed rounded-xl p-4 text-center cursor-pointer transition-all duration-200
            ${isDragging
              ? 'border-emerald-400 bg-emerald-50'
              : disabled
                ? 'border-zinc-200 bg-zinc-50 cursor-not-allowed'
                : 'border-zinc-300 hover:border-zinc-400 hover:bg-zinc-50'
            }
          `}
        >
          <div className="flex items-center justify-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-zinc-100 flex items-center justify-center flex-shrink-0">
              {isExcel ? (
                <FileXls className="w-5 h-5 text-zinc-400" weight="fill" />
              ) : (
                <Table className="w-5 h-5 text-zinc-400" weight="fill" />
              )}
            </div>
            <div className="text-left">
              <p className="text-sm font-medium text-zinc-700">
                {disabled ? t('workflow.waitingForExecution') : t('workflow.clickOrDragFile')}
              </p>
              <p className="text-[10px] text-zinc-400 mt-0.5">
                {t('workflow.supportsFormat', { format: accept })}
              </p>
            </div>
          </div>
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="flex items-center gap-3 p-3 bg-emerald-50 border border-emerald-200 rounded-xl"
        >
          <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
            {isExcel ? (
              <FileXls className="w-5 h-5 text-emerald-600" weight="fill" />
            ) : (
              <Table className="w-5 h-5 text-emerald-600" weight="fill" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-emerald-900 truncate">{node.file.name}</p>
            <p className="text-[10px] text-emerald-600">
              {(node.file.size / 1024).toFixed(1)} KB · {t('workflow.ready')}
            </p>
          </div>
          {!disabled && (
            <button
              onClick={(e) => { e.stopPropagation(); onClear(); }}
              className="p-1.5 text-emerald-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
            >
              <XCircle className="w-4 h-4" />
            </button>
          )}
        </motion.div>
      )}
    </motion.div>
  );
}

// ── Workflow Card ─────────────────────────────────────────────────────────────
const WorkflowCard = forwardRef<
  HTMLDivElement,
  {
    workflow: SavedWorkflow;
    onEdit: (wf: SavedWorkflow) => void;
    onDelete: (wf: SavedWorkflow) => void;
    onManageApps: (wf: SavedWorkflow) => void;
  }
>(function WorkflowCard(
  { workflow, onEdit, onDelete, onManageApps },
  ref
) {
  const { t } = useTranslation();
  return (
    <motion.div
      ref={ref}
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      whileHover={{ y: -2 }}
      className="group bg-white rounded-2xl border border-zinc-200/50 shadow-sm hover:shadow-lg hover:shadow-zinc-900/5 transition-all duration-300 overflow-hidden"
    >
      <div className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-500/20">
              <FlowArrow className="w-5 h-5 text-white" weight="fill" />
            </div>
            <div>
              <h3 className="font-semibold text-zinc-900 group-hover:text-emerald-600 transition-colors">
                {workflow.name}
              </h3>
              <p className="text-xs text-zinc-500 mt-0.5">ID: {workflow.id.slice(-8)}</p>
            </div>
          </div>
        </div>

        {workflow.description && (
          <p className="text-sm text-zinc-600 line-clamp-2 mb-3">{workflow.description}</p>
        )}

        {/* File info */}
        {workflow.uploadedFile && (
          <div className="flex items-center gap-2 mb-3 px-2.5 py-1.5 bg-blue-50 rounded-lg border border-blue-100 w-fit">
            <FileXls className="w-3.5 h-3.5 text-blue-500" weight="fill" />
            <span className="text-xs text-blue-700 truncate max-w-[18ch]">
              {workflow.uploadedFile.name}
            </span>
          </div>
        )}

        <div className="flex items-center gap-4 text-xs text-zinc-500 mb-3">
          <div className="flex items-center gap-1">
            <DotsThree className="w-4 h-4" />
            <span>{t('workflow.nodeCount', { count: workflow.nodeCount })}</span>
          </div>
          <div className="flex items-center gap-1">
            <Play className="w-4 h-4" />
            <span>{t('workflow.executionCount', { count: workflow.executionCount })}</span>
          </div>
          {workflow.requiredFields && workflow.requiredFields.length > 0 && (
            <div className="flex items-center gap-1" title={workflow.requiredFields.join(', ')}>
              <ListChecks className="w-4 h-4" />
              <span>{t('workflow.requiredFields')}: {workflow.requiredFields.length}</span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-3 text-xs text-zinc-400 mb-3">
          <div className="flex items-center gap-1">
            <Calendar className="w-3.5 h-3.5" />
            <span>{new Date(workflow.createdAt).toLocaleDateString('zh-CN')}</span>
          </div>
          {workflow.lastExecutedAt && (
            <div className="flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" />
              <span>{t('workflow.lastExecuted')} {new Date(workflow.lastExecutedAt).toLocaleDateString('zh-CN')}</span>
            </div>
          )}
        </div>

        {/* 标签行 */}
        <div className="flex items-center gap-2">
          <VisibilityBadge visibility={workflow.visibility} isOwner={workflow.teamId ? false : true} />
          <StatusBadge status={workflow.status} />
        </div>
      </div>

      <div className="px-5 py-3 bg-zinc-50/50 border-t border-zinc-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <motion.button
            whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
            onClick={() => onManageApps(workflow)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-zinc-600 bg-white border border-zinc-200 rounded-lg hover:border-emerald-300 hover:text-emerald-600 transition-colors"
          >
            <AppWindow className="w-3.5 h-3.5" />
            {t('workflow.apply')}
          </motion.button>
          <motion.button
            whileHover={{ scale: workflow.canEdit !== false ? 1.05 : 1 }} whileTap={{ scale: workflow.canEdit !== false ? 0.95 : 1 }}
            onClick={() => workflow.canEdit !== false && onEdit(workflow)}
            disabled={workflow.canEdit === false}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-white border rounded-lg transition-colors ${
              workflow.canEdit === false
                ? 'text-zinc-400 border-zinc-200 cursor-not-allowed opacity-50'
                : 'text-zinc-600 border-zinc-200 hover:border-emerald-300 hover:text-emerald-600'
            }`}
            title={workflow.canEdit === false ? t('workflow.noEditPermission') || '无编辑权限' : undefined}
          >
            <PencilSimple className="w-3.5 h-3.5" />
            {t('common.edit')}
          </motion.button>
        </div>
        <div className="flex items-center gap-2">
          <motion.button
            whileHover={{ scale: workflow.canEdit !== false ? 1.05 : 1 }} whileTap={{ scale: workflow.canEdit !== false ? 0.95 : 1 }}
            onClick={() => workflow.canEdit !== false && onDelete(workflow)}
            disabled={workflow.canEdit === false}
            className={`p-1.5 rounded-lg transition-colors ${
              workflow.canEdit === false
                ? 'text-zinc-300 cursor-not-allowed'
                : 'text-zinc-400 hover:text-red-500 hover:bg-red-50'
            }`}
            title={workflow.canEdit === false ? t('workflow.noDeletePermission') || '无删除权限' : undefined}
          >
            <Trash className="w-4 h-4" />
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
});
WorkflowCard.displayName = 'WorkflowCard';

// ── Main Page ─────────────────────────────────────────────────────────────────
export function WorkflowManagementPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { confirm, dialog: confirmDialog } = useConfirm();
  const toast = useToastActions();
  const { workflows, deleteWorkflow, fetchWorkflows } = useWorkflowListStore();
  const [loading, setLoading] = useState(true);

  // 仅进入页面时拉取一次列表，避免依赖变化导致重复请求
  React.useEffect(() => {
    let cancelled = false;
    fetchWorkflows()
      .catch((e) => {
        if (!cancelled) {
          console.error('Load workflows failed:', e);
          toast.error(t('workflow.loadFailed') || '加载表格流列表失败');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 仅 mount 时请求一次
  }, []);

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [runningWorkflow, setRunningWorkflow] = useState<SavedWorkflow | undefined>();
  const [isRunModalOpen, setIsRunModalOpen] = useState(false);

  const filteredWorkflows = workflows.filter((wf) => {
    const matchesSearch =
      wf.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      wf.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || wf.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleCreate = () => navigate('/home');

  const handleEdit = (wf: SavedWorkflow) => navigate(`/workflows/store/edit/${wf.id}`);

  const handleDelete = async (wf: SavedWorkflow) => {
    const confirmed = await confirm({
      title: t('workflow.deleteConfirm') || '删除表格流',
      description: `${t('workflow.deleteDescription', { name: wf.name }) || `确定要删除表格流 "${wf.name}" 吗？此操作不可撤销。`}`,
      confirmText: t('common.confirm') || '确认删除',
      cancelText: t('common.cancel') || '取消',
      type: 'danger',
      requireTextMatch: wf.name,
      matchInputLabel:
        t('workflow.deleteTypeNameLabel', { name: wf.name }) || `请输入工作流名称以确认删除：${wf.name}`,
      matchInputPlaceholder:
        t('workflow.deleteTypeNamePlaceholder') || '输入工作流名称',
      matchErrorText:
        t('workflow.deleteTypeNameMismatch') || '输入的工作流名称不匹配',
    });
    if (confirmed) {
      try {
        await deleteWorkflow(wf.id);
        toast.success(t('workflow.deleteSuccess') || '表格流已删除');
      } catch (e) {
        console.error('Delete workflow failed:', e);
        toast.error(t('workflow.deleteFailed') || '删除失败');
      }
    }
  };

  const handleManageApps = (wf: SavedWorkflow) => {
    setRunningWorkflow(wf);
    setIsRunModalOpen(true);
  };

  return (
    <div className="h-[calc(100vh-4rem)] bg-zinc-50 overflow-y-auto">
      <div className="max-w-[1400px] mx-auto p-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-zinc-900 tracking-tight">{t('workflow.title') || '表格流管理'}</h1>
              <p className="mt-2 text-zinc-500">{t('workflow.subtitle') || '管理已保存的表格流，执行并下载结果'}</p>
            </div>
            <Button
              variant="primary"
              size="lg"
              leftIcon={<Plus className="w-5 h-5" />}
              onClick={handleCreate}
            >
              {t('workflow.create') || '创建表格流'}
            </Button>
          </div>
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-6 flex flex-wrap items-center gap-4"
        >
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlass className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
            <input
              type="text"
              placeholder={t('workflow.searchPlaceholder') || '搜索表格流名称或描述...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-white border border-zinc-200 rounded-xl text-sm text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all shadow-sm"
            />
          </div>

          <div className="flex items-center gap-2">
            {[
              { key: 'all',      label: t('workflow.filterAll') || '全部' },
              { key: 'active',   label: t('workflow.filterActive') || '已启用' },
              { key: 'draft',    label: t('workflow.filterDraft') || '草稿' },
              { key: 'inactive', label: t('workflow.filterInactive') || '已停用' },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setStatusFilter(key)}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                  statusFilter === key
                    ? 'bg-zinc-900 text-white'
                    : 'bg-white text-zinc-600 border border-zinc-200 hover:border-zinc-300'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Spinner className="w-8 h-8 text-emerald-500" />
          </div>
        ) : filteredWorkflows.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex flex-col items-center justify-center py-24 text-center"
          >
            <div className="w-20 h-20 rounded-3xl bg-zinc-100 flex items-center justify-center mb-6">
              <FlowArrow className="w-10 h-10 text-zinc-300" />
            </div>
            <h3 className="text-lg font-semibold text-zinc-900 mb-2">
              {searchQuery ? t('workflow.noMatchingWorkflows') : t('workflow.noWorkflows')}
            </h3>
            <p className="text-sm text-zinc-500 max-w-[40ch] mb-6">
              {searchQuery
                ? t('workflow.adjustSearch')
                : t('workflow.createFirstHint')}
            </p>
            {!searchQuery && (
              <Button variant="primary" leftIcon={<Plus className="w-4 h-4" />} onClick={handleCreate}>
                {t('workflow.create')}
              </Button>
            )}
          </motion.div>
        ) : (
          <motion.div layout className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <AnimatePresence mode="popLayout">
              {filteredWorkflows.map((wf) => (
                <WorkflowCard
                  key={wf.id}
                  workflow={wf}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onManageApps={handleManageApps}
                />
              ))}
            </AnimatePresence>
          </motion.div>
        )}

        {/* Summary */}
        {workflows.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="mt-12 pt-8 border-t border-zinc-200"
          >
            <div className="flex flex-wrap items-center justify-between gap-4 text-sm text-zinc-500">
              <p>
                {t('workflow.totalWorkflows', { count: workflows.length })}
                {filteredWorkflows.length !== workflows.length && (
                  <span>{t('workflow.showingCount', { count: filteredWorkflows.length })}</span>
                )}
              </p>
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-emerald-500" />
                  <span>{t('workflow.activeCount', { count: workflows.filter((w) => w.status === 'active').length })}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-amber-500" />
                  <span>{t('workflow.draftCount', { count: workflows.filter((w) => w.status === 'draft').length })}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-zinc-400" />
                  <span>{t('workflow.inactiveCount', { count: workflows.filter((w) => w.status === 'inactive').length })}</span>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* Run Workflow Modal */}
      <RunWorkflowModal
        isOpen={isRunModalOpen}
        onClose={() => setIsRunModalOpen(false)}
        workflow={runningWorkflow}
      />

      {confirmDialog}
    </div>
  );
}

// 导出组件
export default WorkflowManagementPage;
